import type { Theme } from '@mui/material';
import { Box, Divider, Typography } from '@mui/material';
import type { ComponentProps } from 'react';
import { cardVariants } from '@/pages/ValuationsV2/theme';
import { combineSx, type SXVariantProps, sxVariants } from '@/utils/combineSx';
import { stringToAttr } from '@/utils/stringToAttr';

const colStyles = {
  col: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '1rem',
  },
  colTitle: (theme: Theme) => ({
    fontSize: theme.fontSizes.smaller2,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
  }),
  subCol: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  subColTitle: {
    textTransform: 'uppercase',
  },

  actions: {
    position: 'relative',
    marginTop: 'auto',
    marginBottom: '-0.5rem',
    display: 'grid',
    width: 'calc(100% + 3rem)',
    placeItems: 'center',
    gridTemplateColumns: 'min-max(0, 1fr)',
    gridAutoFlow: 'column',
    gridAutoColumns: 'min-content',
    color: 'inherit',
    '--borderColor': 'color-mix(in srgb, currentColor 15%, transparent)',
    borderTop: `1px solid var(--borderColor)`,
    '& :is(button, a)': {
      color: 'inherit',
      borderRadius: 0,
      border: 0,
      '&:not(:last-child)': {
        borderRight: `1px solid var(--borderColor)`,
      },
    },
  },
};

const colVariants = sxVariants({
  base: combineSx(cardVariants({ interactive: true, padding: 'compact' }), colStyles.col),
  variants: {
    variant: {
      default: {},
      highlighted: {
        position: 'relative',
        color: theme => `${theme.palette.baseColors.deepSeaNavy.c100}`,
        '&:before': {
          position: 'absolute',
          zIndex: 0,
          left: '50%',
          top: '50%',
          translate: '-50% -50% 0',
          content: '" "',
          width: 'calc(100% - 1rem)',
          height: 'calc(100% - 1rem)',
          borderRadius: '0.12rem',
          background: theme => `${theme.palette.baseColors.skyBlue.c25}`,
          boxShadow: 'none',
          opacity: 1,
        },
      },
    },
  },
});

const colTitleVariants = sxVariants({
  base: colStyles.colTitle,
  variants: {
    variant: {
      default: {},
      highlighted: {
        color: 'inherit',
      },
    },
  },
});

type ColProps = SXVariantProps<typeof colVariants> &
  ComponentProps<typeof Box> & {
    columnTitle: string;
    actions?: React.ReactNode;
  };

const Col = ({
  columnTitle,
  children,
  actions = null,
  variant = 'default',
  sx,
  ...props
}: ColProps) => {
  return (
    <Box
      component='section'
      sx={combineSx(colVariants({ variant }), sx)}
      data-id={`summary-column-${stringToAttr(columnTitle)}`}
      {...props}
    >
      <Typography variant='overline' sx={colTitleVariants({ variant })}>
        {columnTitle}
      </Typography>

      {children}
      {actions ? <Box sx={colStyles.actions}>{actions}</Box> : null}
    </Box>
  );
};

type SubColProps = ComponentProps<typeof Box> & {
  subColumnTitle: string;
  textTransform?: 'none' | 'uppercase';
};

const SubCol = ({
  subColumnTitle,
  textTransform = 'uppercase',
  children,
  ...props
}: SubColProps & { children: React.ReactNode }) => {
  return (
    <Box
      sx={colStyles.subCol}
      data-id={`summary-subcolumn-${stringToAttr(subColumnTitle)}`}
      {...props}
    >
      <Typography variant='overline' sx={{ textTransform }}>
        {subColumnTitle}
      </Typography>
      <Divider />
      {children}
    </Box>
  );
};

const colActionsVariants = sxVariants({
  base: colStyles.actions,
  variants: {
    variant: {
      highlighted: {
        width: 'calc(100% + 2rem)',
        marginBottom: '-0.25rem',
      },
      default: {},
    },
  },
});

type ColActionsProps = SXVariantProps<typeof colActionsVariants> &
  ComponentProps<typeof Box> & {
    actions?: React.ReactNode;
  };

const ColActions = ({ children, sx, variant = 'default' }: ColActionsProps) => {
  return <Box sx={combineSx(colActionsVariants({ variant }), sx)}>{children}</Box>;
};

export { Col, SubCol, ColActions };
